import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full rounded-xl border border-[#D8D3C5] bg-white px-4 py-3 text-sm sm:text-base text-[#1A251E] outline-none transition-colors duration-300 focus:border-[#1A382B] placeholder:text-[#6F7E73]";
const LABEL = "block mb-1.5 font-jbm text-xs tracking-wider uppercase text-[#5A826B]";

export default function MeterForm() {
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const emptyForm = {
    water_user_number: "", first_name: "", last_name: "", parcel: "", street: "",
    phone: "", email: "", reading_date: "", meter_value: "", meter_number: "",
    calibration_date: "", reading_type: "zwischenablesung", note: "",
  };
  const [form, setForm] = useState(emptyForm);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!confirmed) {
      toast.error("Bestätigung fehlt", {
        description: "Bitte bestätigen Sie zuerst, dass Zähler, Wasserleitungen und Absperrarmaturen technisch in Ordnung sind.",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/meter-reading`, { ...form, technical_ok: true });
      toast.success("Zählerstand übermittelt", {
        description: "Vielen Dank! Ihre Angaben wurden an den Verein übermittelt.",
      });
      setForm(emptyForm);
      setConfirmed(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Übermittlung fehlgeschlagen", {
        description: typeof detail === "string" ? detail : "Bitte prüfen Sie Ihre Angaben und versuchen Sie es erneut.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#D8D3C5] bg-white p-6 sm:p-8 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="meter-reading-form">
      <h3 className="font-display text-2xl font-semibold text-[#1A382B]">Zählerstand übermitteln</h3>
      <p className="mt-2 mb-6 text-sm leading-relaxed text-[#47544A]">
        Wenn Sie bei den Ablesungen nicht anwesend sein können – z. B. bei Urlaub, Krankheit oder als
        Neupächter – übermitteln Sie Ihren Zählerstand hier direkt an den Verein. Das spart Papier und
        beschleunigt die Vereinsarbeit. Alle Felder mit * sind Pflichtfelder.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={LABEL}>Wassernutzernummer</label>
          <input className={FIELD} value={form.water_user_number} onChange={set("water_user_number")} placeholder="Aus Wasservertrag oder Jahresrechnung" data-testid="meter-input-user-number" />
        </div>
        <div>
          <label className={LABEL}>Parzelle (nicht Hausnummer!)</label>
          <input className={FIELD} value={form.parcel} onChange={set("parcel")} placeholder="z. B. 42" data-testid="meter-input-parcel" />
        </div>
        <div>
          <label className={LABEL}>Vorname *</label>
          <input required className={FIELD} value={form.first_name} onChange={set("first_name")} data-testid="meter-input-first-name" />
        </div>
        <div>
          <label className={LABEL}>Nachname *</label>
          <input required className={FIELD} value={form.last_name} onChange={set("last_name")} data-testid="meter-input-last-name" />
        </div>
        <div>
          <label className={LABEL}>Weg und Hausnummer *</label>
          <input required className={FIELD} value={form.street} onChange={set("street")} data-testid="meter-input-street" />
        </div>
        <div>
          <label className={LABEL}>Telefonnummer *</label>
          <input required type="tel" className={FIELD} value={form.phone} onChange={set("phone")} placeholder="Für evtl. Rückfragen" data-testid="meter-input-phone" />
        </div>
        <div>
          <label className={LABEL}>E-Mail-Adresse *</label>
          <input required type="email" className={FIELD} value={form.email} onChange={set("email")} data-testid="meter-input-email" />
        </div>
        <div>
          <label className={LABEL}>Datum der Ablesung *</label>
          <input required type="date" className={FIELD} value={form.reading_date} onChange={set("reading_date")} data-testid="meter-input-date" />
        </div>
        <div>
          <label className={LABEL}>Zählerstand *</label>
          <input required className={FIELD} value={form.meter_value} onChange={set("meter_value")} placeholder="z. B. 001234,5" data-testid="meter-input-value" />
        </div>
        <div>
          <label className={LABEL}>Zählernummer *</label>
          <input required className={FIELD} value={form.meter_number} onChange={set("meter_number")} placeholder="Am Rand des Zählers" data-testid="meter-input-number" />
        </div>
        <div className="sm:col-span-2">
          <label className={LABEL}>Eichdatum / Einbaudatum des Zählers</label>
          <input className={FIELD} value={form.calibration_date} onChange={set("calibration_date")} placeholder="z. B. 03/2019" data-testid="meter-input-calibration-date" />
        </div>
        <div className="sm:col-span-2">
          <label className={LABEL}>Was möchten Sie übermitteln? *</label>
          <select className={FIELD} value={form.reading_type} onChange={set("reading_type")} data-testid="meter-input-type">
            <option value="anmeldung">Zählerstand – Neue Anmeldung</option>
            <option value="zwischenablesung">Zählerstand – Zwischenablesung</option>
            <option value="abmeldung">Zählerstand – Abmeldung</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={LABEL}>Bemerkung / Info</label>
          <textarea rows={3} className={FIELD} value={form.note} onChange={set("note")} data-testid="meter-input-note" />
        </div>
      </div>
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-[#D8D3C5] bg-[#F8F7F2] px-4 py-3 flex-1" data-testid="meter-confirm-label">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="meter-confirm-checkbox"
            className="mt-0.5 h-5 w-5 shrink-0 accent-[#1A382B]"
          />
          <span className="text-sm leading-snug text-[#2A332E]">
            Zähler, Wasserleitungen und Absperrarmaturen sind technisch in Ordnung
          </span>
        </label>
        <button
          type="submit"
          disabled={loading}
          data-testid="meter-submit-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1A382B] px-7 py-3.5 text-sm font-semibold text-[#F8F7F2] transition-all duration-300 hover:bg-[#C85A32] hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          Zählerstand senden
        </button>
      </div>
    </form>
  );
}
